import { useEffect, useState } from "react";
import Select from "react-select";
const EMPTY_FORM = {
    report_id: 1,
    activity_id: null,
    plan_date: "",
    planned_man: 0,
    actual_man: 0,
    remark: "",
    shift_id: null,
    machine_id: null,
    die_id: null,
    plan_qty: 0,
    product_id: null,
    comp_code: 2
};

const SHIFT_OPTIONS = [
    {
        value: 1,
        label: "Shift-A"
    },
    {
        value: 2,
        label: "Shift-B"
    }
];

const ProductionDailyActivityModal = ({
    activity,
    machines = [],
    dies = [],
    products = [],
    activityMasters = [],
    listLoading = false,
    loading,
    onClose,
    onCreate,
    onUpdate
}) => {

    const [formData, setFormData] = useState(EMPTY_FORM);
    const [error, setError] = useState("");

    // =========================================================
    // LOAD EDIT DATA
    // =========================================================

    useEffect(() => {

        if (activity) {

            setFormData({
                report_id: activity.report_id ?? 1,

                activity_id: activity.activity_id != null
                    ? Number(activity.activity_id)
                    : null,

                plan_date: activity.plan_date
                    ? String(activity.plan_date).substring(0, 10)
                    : "",

                planned_man: activity.planned_man ?? 0,

                actual_man: activity.actual_man ?? 0,

                remark: activity.remark ?? "",

                shift_id: activity.shift_id != null
                    ? Number(activity.shift_id)
                    : null,

                machine_id: activity.machine_id != null
                    ? Number(activity.machine_id)
                    : null,

                die_id: activity.die_id != null
                    ? Number(activity.die_id)
                    : null,

                plan_qty: activity.plan_qty ?? 0,

                product_id: activity.product_id != null
                    ? Number(activity.product_id)
                    : null,

                comp_code: activity.comp_code ?? 2
            });

        } else {

            setFormData({
                ...EMPTY_FORM
            });

        }

        setError("");

    }, [activity]);


    // =========================================================
    // ACTIVITY OPTIONS
    // =========================================================

    const activityOptions = activityMasters.map((item) => ({
        value: Number(item.id),
        label: item.activity_name
    }));


    // =========================================================
    // MACHINE OPTIONS
    // =========================================================

    const machineOptions = machines.map((item) => ({
        value: Number(item.id),
        label: item.machine_code
    }));


    // =========================================================
    // DIE OPTIONS
    // =========================================================

    const dieOptions = dies
        .filter((item) => item.type)
        .map((item) => ({
            value: Number(item.id),
            label: item.type
        }));


    // =========================================================
    // PRODUCT OPTIONS
    // =========================================================

    const productOptions = products.map((item) => ({
        value: Number(item.id),
        label: item.name
    }));


    // =========================================================
    // GENERIC SELECT CHANGE
    // =========================================================

    const handleSelectChange = (name, selectedOption) => {

        setFormData((prev) => ({
            ...prev,

            [name]: selectedOption
                ? Number(selectedOption.value)
                : null
        }));

    };


    // =========================================================
    // NORMAL INPUT CHANGE
    // =========================================================

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));

    };


    // =========================================================
    // SHIFT CHANGE
    // =========================================================

    const handleShiftChange = (e) => {

        const value = e.target.value;

        setFormData((prev) => ({
            ...prev,

            shift_id: value
                ? Number(value)
                : null
        }));

    };


    // =========================================================
    // SUBMIT
    // =========================================================

    const handleSubmit = async (e) => {

        e.preventDefault();

        setError("");

        // -----------------------------------------------------
        // Create clean payload
        // -----------------------------------------------------

        const payload = {

            report_id: formData.report_id
                ? Number(formData.report_id)
                : null,

            activity_id: formData.activity_id
                ? Number(formData.activity_id)
                : null,

            plan_date: formData.plan_date || null,

            planned_man: formData.planned_man !== ""
                ? Number(formData.planned_man)
                : null,

            actual_man: formData.actual_man !== ""
                ? Number(formData.actual_man)
                : null,

            remark: formData.remark || null,

            shift_id: formData.shift_id
                ? Number(formData.shift_id)
                : null,

            machine_id: formData.machine_id
                ? Number(formData.machine_id)
                : null,

            die_id: formData.die_id
                ? Number(formData.die_id)
                : null,

            plan_qty: formData.plan_qty !== ""
                ? Number(formData.plan_qty)
                : null,

            product_id: formData.product_id
                ? Number(formData.product_id)
                : null,

            comp_code: formData.comp_code
                ? String(formData.comp_code)
                : null
        };

        // -----------------------------------------------------
        // CHECK PAYLOAD
        // -----------------------------------------------------

        console.log(
            "Production Daily Activity Payload:",
            payload
        );

        try {

            if (activity) {

                await onUpdate(
                    activity.id,
                    payload
                );

            } else {

                await onCreate(payload);

            }

        } catch (error) {

            console.error(
                "ProductionDailyActivityModal:",
                error
            );

            const response = error.response?.data;

            // Laravel validation errors
            if (response?.errors) {

                const validationMessages = Object.entries(
                    response.errors
                )
                    .map(([field, messages]) => {
                        return `${field}: ${messages.join(", ")}`;
                    })
                    .join("\n");

                setError(validationMessages);

            } else {

                setError(
                    response?.message ||
                    "Failed to save activity."
                );

            }

        }

    };


    // =========================================================
    // SELECT STYLES
    // =========================================================

    const selectStyles = {

        control: (base, state) => ({
            ...base,

            minHeight: "38px",

            borderColor: state.isFocused
                ? "#86b7fe"
                : "#dee2e6",

            boxShadow: state.isFocused
                ? "0 0 0 0.25rem rgba(13,110,253,.25)"
                : "none",

            "&:hover": {
                borderColor: "#86b7fe"
            }
        }),

        menu: (base) => ({
            ...base,
            zIndex: 9999
        })
    };


    return (

        <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{
                backgroundColor: "rgba(0,0,0,0.5)"
            }}
        >

            <div className="modal-dialog modal-xl modal-dialog-centered">

                <div className="modal-content">

                    {/* =================================================
                        HEADER
                    ================================================= */}

                    <div className="modal-header">

                        <h5 className="modal-title">

                            {activity
                                ? "Edit Production Daily Activity"
                                : "Add Production Daily Activity"}

                        </h5>

                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            disabled={loading}
                        />

                    </div>


                    {/* =================================================
                        FORM
                    ================================================= */}

                    <form onSubmit={handleSubmit}>

                        <div className="modal-body">

                            {/* ERROR */}

                            {error && (

                                <div
                                    className="alert alert-danger"
                                    style={{
                                        whiteSpace: "pre-line"
                                    }}
                                >
                                    {error}
                                </div>

                            )}


                            {/* LOADING */}

                            {listLoading && (

                                <div className="alert alert-info">

                                    Loading master data...

                                </div>

                            )}


                            <div className="row g-3">


                                {/* =================================================
                                    ACTIVITY
                                ================================================= */}

                                <div className="col-md-3">

                                    <label className="form-label">
                                        Select Activity/Process
                                    </label>

                                    <Select
                                        options={activityOptions}

                                        value={
                                            activityOptions.find(
                                                option =>
                                                    Number(option.value) ===
                                                    Number(formData.activity_id)
                                            ) || null
                                        }

                                        onChange={(selected) =>
                                            handleSelectChange(
                                                "activity_id",
                                                selected
                                            )
                                        }

                                        isSearchable
                                        isClearable
                                        isDisabled={listLoading}

                                        placeholder="Search Activity..."

                                        noOptionsMessage={() =>
                                            "No activity found"
                                        }

                                        styles={selectStyles}
                                    />

                                </div>


                                {/* =================================================
                                    PLAN DATE
                                ================================================= */}

                                <div className="col-md-2">

                                    <label className="form-label">
                                        Plan Date
                                    </label>

                                    <input
                                        type="date"
                                        className="form-control"
                                        name="plan_date"
                                        value={formData.plan_date}
                                        onChange={handleChange}
                                        disabled={loading}
                                    />

                                </div>


                                {/* =================================================
                                    MACHINE
                                ================================================= */}

                                <div hidden className="col-md-3">

                                    <label className="form-label">
                                        Machine
                                    </label>

                                    <Select
                                        options={machineOptions}

                                        value={
                                            machineOptions.find(
                                                option =>
                                                    Number(option.value) ===
                                                    Number(formData.machine_id)
                                            ) || null
                                        }

                                        onChange={(option) =>
                                            handleSelectChange(
                                                "machine_id",
                                                option
                                            )
                                        }

                                        isClearable
                                        isSearchable
                                        isLoading={listLoading}

                                        placeholder="Search machine..."

                                        noOptionsMessage={() =>
                                            "No machine found"
                                        }

                                        styles={selectStyles}
                                    />

                                </div>


                                {/* =================================================
                                    DIE
                                ================================================= */}

                                <div hidden className="col-md-2">

                                    <label className="form-label">
                                        Die
                                    </label>

                                    <Select
                                        options={dieOptions}

                                        value={
                                            dieOptions.find(
                                                option =>
                                                    Number(option.value) ===
                                                    Number(formData.die_id)
                                            ) || null
                                        }

                                        onChange={(option) =>
                                            handleSelectChange(
                                                "die_id",
                                                option
                                            )
                                        }

                                        isSearchable
                                        isClearable
                                        isDisabled={listLoading}

                                        placeholder="Search die..."

                                        noOptionsMessage={() =>
                                            "No die found"
                                        }

                                        styles={selectStyles}
                                    />

                                </div>


                                {/* =================================================
                                    PRODUCT
                                ================================================= */}

                                <div hidden className="col-md-3">

                                    <label className="form-label">
                                        Product
                                    </label>

                                    <Select
                                        options={productOptions}

                                        value={
                                            productOptions.find(
                                                option =>
                                                    Number(option.value) ===
                                                    Number(formData.product_id)
                                            ) || null
                                        }

                                        onChange={(option) =>
                                            handleSelectChange(
                                                "product_id",
                                                option
                                            )
                                        }

                                        isClearable
                                        isSearchable
                                        isLoading={listLoading}

                                        placeholder="Search product..."

                                        noOptionsMessage={() =>
                                            "No product found"
                                        }

                                        styles={selectStyles}
                                    />

                                </div>


                                {/* =================================================
                                    PLANNED MAN
                                ================================================= */}

                                <div className="col-md-2">

                                    <label className="form-label">
                                        Planned Man Power
                                    </label>

                                    <input
                                        type="number"
                                        min="0"
                                        className="form-control"
                                        name="planned_man"
                                        value={formData.planned_man}
                                        onChange={handleChange}
                                    />

                                </div>


                                {/* =================================================
                                    ACTUAL MAN
                                ================================================= */}

                                <div className="col-md-2">

                                    <label className="form-label">
                                        Actual Man Power
                                    </label>

                                    <input
                                        type="number"
                                        min="0"
                                        className="form-control"
                                        name="actual_man"
                                        value={formData.actual_man}
                                        onChange={handleChange}
                                    />

                                </div>


                                {/* =================================================
                                    PLAN QUANTITY
                                ================================================= */}

                                <div hidden className="col-md-2">

                                    <label className="form-label">
                                        Plan Quantity
                                    </label>

                                    <input
                                        type="number"
                                        min="0"
                                        className="form-control"
                                        name="plan_qty"
                                        value={formData.plan_qty}
                                        onChange={handleChange}
                                    />

                                </div>


                                {/* =================================================
                                    SHIFT
                                ================================================= */}

                                <div className="col-md-2">

                                    <label className="form-label">
                                        Shift
                                    </label>

                                    <select
                                        className="form-select"
                                        name="shift_id"
                                        value={
                                            formData.shift_id ?? ""
                                        }
                                        onChange={handleShiftChange}
                                        disabled={loading}
                                    >

                                        <option value="">
                                            -- Select Shift --
                                        </option>

                                        {SHIFT_OPTIONS.map((shift) => (

                                            <option
                                                key={shift.value}
                                                value={shift.value}
                                            >
                                                {shift.label}
                                            </option>

                                        ))}

                                    </select>

                                </div>


                                {/* =================================================
                                    REMARK
                                ================================================= */}

                                <div className="col-md-6">

                                    <label className="form-label">
                                        Remark
                                    </label>

                                    <textarea
                                        className="form-control"
                                        rows="1"
                                        name="remark"
                                        value={formData.remark}
                                        onChange={handleChange}
                                    />

                                </div>

                            </div>

                        </div>


                        {/* =================================================
                            FOOTER
                        ================================================= */}

                        <div className="modal-footer">

                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={
                                    loading ||
                                    listLoading
                                }
                            >

                                {loading
                                    ? "Saving..."
                                    : activity
                                        ? "Update Activity"
                                        : "Save Activity"}

                            </button>

                        </div>

                    </form>

                </div>

            </div>

        </div>
    );
};

export default ProductionDailyActivityModal;

